import { useRef, useState, type FormEvent, useId } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { ErrorBox, Page, useApi } from '../ui/kit';
import { AdsCard } from '../ui/ads-parts';
import { usePaywallSeen, WallButtons, type WallAction } from '../ui/credit-wall';
import { firstName } from '../lib/format';
import { newChat, type ChatKind } from '../ui/board';
import { integrationBySlug } from '../../../src/integrations/catalog';

// Home in the new experience (as Bridge's start page): a greeting, today's message, a few ways in and one big input.
// The "Today" message is phrased as the assistant's but built from the data (GET /portal/today); no model writes it.
// What Home leads with depends on the workspace: a new one leads with "Finish setup (3 of 7)".

export type TodayLine = { tone: 'good' | 'warn' | 'info'; text: string; link?: string; label?: string; group?: 'needs' | 'yesterday'; actions?: WallAction[] };
type Today = {
  lines: TodayLine[];
  setup: { done: number; total: number; next: { key: string; title: string } | null; complete: boolean };
  counts: { drafts: number; active: number; accounts: number; replies: number; approvals: number };
};

/** Home's guess that a question is the setup path's (the server's guessKind, first_campaign). */
const guessSetup = (text: string) => /would buy from me|who (should|do) i (write|email|reach)|who to write|to sell to\b|my first campaign|no idea who/i.test(text);
const greeting = () => { const h = new Date().getHours(); return h < 5 ? 'Good evening' : h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'; };
const P = (d: string) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>;

export function Launchpad() {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const { session, me } = useAuth();
  const nav = useNavigate();
  const t = useApi<Today>('/portal/today', { refetchMs: 120_000 });
  const campaigns = useApi<{ data: Array<{ id: string; name: string; status: string }> }>('/v1/campaigns?limit=10');
  // what this workspace can connect now and has not: the CRM, ad accounts, LinkedIn and a calendar first (owner request 2026-10-23)
  const market = useApi<{ marketplace?: Array<{ slug: string; status: string; connected: boolean }> }>('/v1/integrations');
  const toConnect = connectSuggestions(market.data?.marketplace);
  const [q, setQ] = useState('');
  const box = useRef<HTMLTextAreaElement>(null);
  const first = firstName(session?.user.email);
  // the first message turns Home into the working view: the workspace's cards on the left, the chat on the right
  const [kind, setKind] = useState<ChatKind | undefined>(undefined);
  // one setup path at a time: a request for it goes to the open one ("I have no idea who…" started a second, 2026-10-31)
  const chats = useApi<{ data: Array<{ subject: string; subject_id: string | null; kind: string | null; brief_state: string | null }> }>('/portal/threads');
  const setup = (chats.data?.data ?? []).find((x) => x.subject === 'task' && x.kind === 'first_campaign' && x.brief_state !== 'done' && x.subject_id);
  const go = (text: string, k?: ChatKind) => {
    if (setup && (k === 'first_campaign' || (!k && guessSetup(text)))) {
      nav(`/chat/${setup.subject_id}`);
      setTimeout(() => window.dispatchEvent(new CustomEvent('olv-assistant-ask', { detail: text })), 50);
      return;
    }
    newChat(nav, text, k);
  };
  const ask = (e: FormEvent) => { e.preventDefault(); if (q.trim()) { go(q.trim(), kind); setQ(''); setKind(undefined); } };
  // a chip ending with "…" starts the sentence for the person to finish; the others ask straight away
  const chip = (c: Chip) => {
    if (c.to) { nav(c.to); if (c.ask) setTimeout(() => window.dispatchEvent(new CustomEvent('olv-assistant-ask', { detail: c.ask })), 50); return; }
    if (c.prefill) { setKind(c.kind); setQ(c.prefill); setTimeout(() => { box.current?.focus(); box.current?.setSelectionRange(c.prefill!.length, c.prefill!.length); }, 0); } else go(c.ask!, c.kind); };
  const c = t.data?.counts;
  const s = t.data?.setup;
  const list = campaigns.data?.data ?? [];
  const draft = list.find((x) => ['draft', 'building', 'built'].includes(x.status));
  const live = list.find((x) => x.status === 'active');
  const groups: Array<{ verb: string; chips: Chip[] }> = [
    { verb: 'Find', chips: [
      // "Companies like…" alone read as "like me" and found competitors: buyers first, lookalikes of a customer second
      // a workspace still setting up gets the whole path (F35): website, buyers, companies, people and channels, launch
      { label: 'Companies to sell to', ask: 'Find companies that would buy from me', kind: s && !s.complete ? 'first_campaign' : 'prospect' },
      { label: 'Companies like a customer…', prefill: 'Find companies like my customer ', kind: 'prospect' },
      { label: 'Decision makers at qualified accounts', ask: 'Find decision makers at my qualified accounts', kind: 'contacts' },
      { label: 'Import a list', ask: 'I want to import a CSV of accounts', kind: 'import' },
    ] },
    { verb: 'Launch', chips: [
      // a new campaign is the campaign board itself: its cards are the brief
      { label: 'New campaign', to: '/campaigns/new', ask: 'Set up a new campaign with me' },
      ...(draft ? [{ label: `Launch “${draft.name}”`, ask: `Launch the campaign "${draft.name}": what is still missing?`, kind: 'launch' as ChatKind }] : []),
    ] },
    { verb: 'Answer', chips: [
      { label: c?.replies ? `${c.replies} repl${c.replies === 1 ? 'y' : 'ies'} this week` : 'Replies', ask: 'Sort my latest replies: interested, question, not now, wrong person, unsubscribe. Draft answers for the interested ones.', kind: 'replies' },
      { label: c?.approvals ? `${c.approvals} waiting for my approval` : 'What’s waiting for me?', ask: 'What is waiting for me?', kind: 'waiting' },
    ] },
    { verb: 'Analyse', chips: [
      ...(live ? [{ label: `How is “${live.name}” doing?`, ask: `How is the campaign "${live.name}" doing?`, kind: 'analysis' as ChatKind }] : []),
      { label: 'Compare my campaigns', ask: 'Compare my campaigns: which works best and why?', kind: 'analysis' },
      { label: 'This week', ask: 'What happened this week: reached, replies, meetings, and what to do next?', kind: 'week' },
    ] },
    { verb: 'Connect', chips: [
      ...toConnect.map((slug): Chip => ({ label: `Connect ${integrationBySlug(slug)?.name ?? slug}`, to: `/integrations/${slug}` })),
      { label: toConnect.length ? 'All integrations' : 'Integrations', to: '/integrations' },
    ] },
    // for people building on Outlevo: the key, the first call, webhooks, MCP, the chat in their own app
    { verb: 'Build', chips: [
      { label: 'Get an API key', ask: 'Set me up with an Outlevo API key for my app and show me the first call', kind: 'developer' },
      { label: 'Connect Claude or Cursor (MCP)', ask: 'Connect Outlevo to my AI coding tool (Claude Code, Cursor or Claude Desktop) through MCP', kind: 'developer' },
      { label: 'Add the Outlevo chat to my app', ask: 'Add the Outlevo assistant chat to my own app', kind: 'developer' },
      { label: 'Get events by webhook', ask: 'Send Outlevo events (replies, qualified companies) to my app by webhook', kind: 'developer' },
      { label: 'Integrate my app…', prefill: 'Help me integrate Outlevo with my app, built with ', kind: 'developer' },
    ] },
  ];
  return (
    <Page title="Home">
      <div className="launchpad">
        {s && !s.complete ? (
          <Link to="/campaigns/new" className="lp-setup">
            <span className="lp-ring" style={{ ['--p' as string]: `${Math.round((s.done / s.total) * 100)}%` }} aria-hidden="true" />
            <span><b>Finish setup ({s.done} of {s.total})</b><small>{s.next ? `Next: ${s.next.title}. The assistant does it with you.` : 'A few steps before your first campaign.'}</small></span>
            <span className="lp-go" aria-hidden="true">→</span>
          </Link>
        ) : null}
        <h2 className="lp-hello">{greeting()}{first ? `, ${first}` : ''}.<span>What should we work on?</span></h2>
        {t.data ? <TodaySection lines={t.data.lines} go={(to) => nav(to)} /> : null}
        <form className="lp-ask" onSubmit={ask}>
          <textarea id={`${uid}-f1`} ref={box} rows={2} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask me anything" aria-label="Ask the assistant"
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(e); } }} />
          <button type="submit" className="as-send" disabled={!q.trim()} aria-label="Send">{P('M12 19V5M5 12l7-7 7 7')}</button>
        </form>
        <div className="lp-starters" role="group" aria-label="Things to ask">
          {groups.map((g) => (
            <div key={g.verb} className="lp-group" role="group" aria-label={g.verb}>
              <span className="lp-verb">{g.verb}</span>
              <div className="lp-chips">{g.chips.map((x) => <button key={x.label} type="button" className="lp-chip" onClick={() => chip(x)}>{x.label}</button>)}</div>
            </div>
          ))}
        </div>
        <p className="lp-foot">The assistant can make mistakes; it never approves or sends, and everything it changes can be undone.</p>
        <section className="hub-campaigns" aria-label="Campaigns">
          <header><b>Campaigns</b>{list.length > 1 ? <button type="button" className="link" onClick={() => go('Compare my campaigns: which works best and why?', 'analysis')}>Compare them</button> : null}</header>
          {list.length ? (
            <ul>{list.map((x) => <li key={x.id}><Link to={`/campaigns/${x.id}`}>{x.name}</Link><span className={`chip ${x.status === 'active' ? 'done' : ''}`}>{x.status}</span></li>)}</ul>
          ) : <p className="muted small">No campaigns yet. <Link to="/campaigns/new">Start one with the assistant</Link></p>}
        </section>
        {/* the ads at a glance and a Run ads entry: the chat-first Home had none, so an ads-first person never saw ads here */}
        <AdsCard />
        <p className="lp-continue"><Link to="/chat">Continue in your workspace →</Link></p>
        {me ? <p className="muted small lp-classic">Prefer the classic dashboard? An owner or admin can switch in the account menu.</p> : null}
      </div>
    </Page>
  );
}

/**
 * Today: the daily brief's content (GET /portal/today builds it with the same function as the email): what needs you,
 * each with its one action, then what happened since yesterday's start; then the other lines of the day (setup, the last 24 hours).
 */
export function TodaySection({ lines, go }: { lines: TodayLine[]; go: (to: string) => void }) {
  // the 0-credit first screen (a card offer) and a credit wall on Home count as walls seen
  usePaywallSeen(lines.some((l) => l.actions?.some((a) => a.id === 'card' || a.kind === 'card' || a.id === 'topup')), 'home', null);
  const groups: Array<{ key: string; title: string | null; lines: TodayLine[] }> = [
    { key: 'needs', title: 'Needs you', lines: lines.filter((l) => l.group === 'needs') },
    { key: 'yesterday', title: 'Since yesterday', lines: lines.filter((l) => l.group === 'yesterday') },
    { key: 'other', title: null, lines: lines.filter((l) => !l.group) },
  ].filter((g) => g.lines.length);
  return (
    <section className="lp-today" aria-label="Today">
      <div className="as-who">Outlevo · today</div>
      {groups.map((g) => (
        <div key={g.key} className={`lp-today-group lp-today-${g.key}`}>
          {g.title ? <h2>{g.title}</h2> : null}
          <ul aria-label={g.title ?? undefined}>{g.lines.map((l, i) => (
            <li key={i} className={`tone-${l.tone}`}>
              <span>{l.text}</span>
              {l.actions?.length ? <WallButtons actions={l.actions} what={l.text} /> : null}
              {l.link ? <button type="button" className="link" onClick={() => go(l.link!)}>{l.label ?? 'Open'}</button> : null}
            </li>
          ))}</ul>
        </div>
      ))}
    </section>
  );
}

const CONNECT_FIRST = ['hubspot', 'google-ads', 'reddit-ads', 'linkedin', 'calendly', 'salesforce', 'linkedin-ads', 'meta-ads', 'x-ads', 'website-visitors', 'smartlead'];
/** Home's Connect chips: up to five integrations this workspace can connect now and has not, the most useful first. */
export const connectSuggestions = (market: Array<{ slug: string; status: string; connected: boolean }> | undefined) =>
  CONNECT_FIRST.filter((slug) => market?.some((m) => m.slug === slug && m.status === 'available' && !m.connected)).slice(0, 5);

type Chip = { label: string; ask?: string; prefill?: string; kind?: ChatKind; to?: string };

type ThreadRow = { id: string; subject: 'workspace' | 'campaign'; subject_id: string | null; title: string; status: string; campaign_status: string | null; last_message_at: string; unread: number };

/** All work: every thread (the workspace, each campaign) in one list, with filters. */
export function AllWork() {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const threads = useApi<{ data: ThreadRow[] }>('/portal/threads', { refetchMs: 30_000 });
  const campaigns = useApi<{ data: Array<{ id: string; name: string; status: string; created_at: string }> }>('/v1/campaigns?limit=100');
  const [kind, setKind] = useState<'all' | 'campaign' | 'workspace'>('all');
  const [status, setStatus] = useState<string>('all');
  const byCampaign = new Map((threads.data?.data ?? []).filter((t) => t.subject_id).map((t) => [t.subject_id!, t]));
  const rows = [
    ...(threads.data?.data ?? []).filter((t) => t.subject === 'workspace').map((t) => ({ key: t.id, kind: 'workspace' as const, title: 'Workspace setup and questions', status: 'open', to: '/chat', unread: t.unread, at: t.last_message_at })),
    ...(campaigns.data?.data ?? []).map((c) => ({ key: c.id, kind: 'campaign' as const, title: c.name, status: c.status, to: `/campaigns/${c.id}`, unread: byCampaign.get(c.id)?.unread ?? 0, at: byCampaign.get(c.id)?.last_message_at ?? c.created_at })),
  ].filter((r) => (kind === 'all' || r.kind === kind) && (status === 'all' || r.status === status))
    .sort((a, b) => b.at.localeCompare(a.at));
  const statuses = ['all', 'draft', 'built', 'active', 'paused', 'stopped'];
  return (
    <Page title="All work" actions={<Link className="btn btn-primary" to="/campaigns/new">+ New campaign</Link>}>
      <div className="aw-filters" role="group" aria-label="Filters">
        <div className="seg-text" role="radiogroup" aria-label="Kind">{(['all', 'campaign', 'workspace'] as const).map((k) => <button key={k} type="button" role="radio" aria-checked={kind === k} onClick={() => setKind(k)}>{k === 'all' ? 'Everything' : k === 'campaign' ? 'Campaigns' : 'Workspace'}</button>)}</div>
        <label>Status <select id={`${uid}-f2`} value={status} onChange={(e) => setStatus(e.target.value)}>{statuses.map((s) => <option key={s} value={s}>{s === 'all' ? 'Any' : s}</option>)}</select></label>
      </div>
      <table className="table aw-table">
        <thead><tr><th>What</th><th>Status</th><th>Last activity</th><th><span className="sr-only">Unread</span></th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.key}>
            <td><Link to={r.to}>{r.title}</Link><small className="muted"> · {r.kind === 'campaign' ? 'campaign' : 'workspace'}</small></td>
            <td><span className={`chip ${r.status === 'active' ? 'done' : ''}`}>{r.status}</span></td>
            <td className="muted">{new Date(r.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
            <td>{r.unread ? <span className="badge" aria-label={`${r.unread} unread`}>{r.unread}</span> : null}</td>
          </tr>
        ))}</tbody>
      </table>
      {threads.error || campaigns.error ? <ErrorBox error={threads.error ?? campaigns.error} />
        : !threads.data ? <p className="muted" role="status">Loading…</p>
        : !rows.length ? <p className="muted">Nothing matches these filters.</p> : null}
    </Page>
  );
}
